import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Alert,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Security,
  Notifications,
  Save,
  Cancel,
  VpnKey,
  Visibility,
  VisibilityOff,
  Refresh,
  CheckCircle,
  Smartphone,
  Analytics,
  RestartAlt
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { userApi } from '../services/api/userApi';
import { authApi } from '../services/api/authApi';
import { AccountType } from '../types/auth';
import PageHeader from '../components/common/PageHeader';
import { TwoFactorSetupDialog } from '../components/auth/TwoFactorSetupDialog';
import { TwoFactorDisableDialog } from '../components/auth/TwoFactorDisableDialog';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { validatePasswordStrength } from '../utils/passwordValidation';

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pushNotificationError, setPushNotificationError] = useState<string | null>(null);
  
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Browser push notification hook
  const {
    isSupported: isPushSupported,
    browserPermission,
    isEnabled: isPushEnabled,
    togglePushNotifications,
  } = usePushNotifications({ enablePolling: false });

  const [settings, setSettings] = useState({
    emailNotifications: false,
    pushNotifications: false,
  });

  // Load saved notification preferences from backend on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;
      try {
        const response = await authApi.getMe();
        const data = response?.data;
        if (data) {
          setSettings(prev => ({
            ...prev,
            emailNotifications: data.emailNotification ?? false,
          }));
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
      }
    };
    loadPreferences();
  }, [user]);

  // Sync the push toggle with the actual browser permission on mount
  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      pushNotifications: isPushEnabled,
    }));
  }, [isPushEnabled]);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState<string | null>(null);
  const [refreshingFull, setRefreshingFull] = useState(false);
  const [fullRebuildSuccess, setFullRebuildSuccess] = useState<string | null>(null);

  const [savingNotification, setSavingNotification] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetupOpen, setTwoFactorSetupOpen] = useState(false);
  const [twoFactorDisableOpen, setTwoFactorDisableOpen] = useState(false);
  const [checking2FAStatus, setChecking2FAStatus] = useState(true);

  // Check 2FA status on mount
  useEffect(() => {
    const check2FAStatus = async () => {
      try {
        const status = await authApi.get2FAStatus();
        setTwoFactorEnabled(status.enabled);
      } catch (error) {
        console.error('Failed to check 2FA status:', error);
      } finally {
        setChecking2FAStatus(false);
      }
    };

    if (user) {
      check2FAStatus();
    }
  }, [user]);

  const handleEnable2FA = () => {
    setTwoFactorSetupOpen(true);
  };

  const handle2FASetupSuccess = () => {
    setTwoFactorEnabled(true);
    setSuccess('Two-factor authentication enabled successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDisable2FA = () => {
    setTwoFactorDisableOpen(true);
  };

  const handle2FADisableSuccess = () => {
    setTwoFactorEnabled(false);
    setSuccess('Two-factor authentication has been disabled.');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleChangePassword = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setError(null);
    setSuccess(null);
    setChangePasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setChangePasswordDialogOpen(false);
    setError(null);
    setSuccess(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSavePassword = async () => {
    if (!user) return;

    // Validation
    if (!passwordData.currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!passwordData.newPassword) {
      setError('New password is required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const passwordError = validatePasswordStrength(passwordData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await userApi.changePassword(user.id, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setSuccess('Password changed successfully! You will be logged out for security reasons.');
      setTimeout(() => {
        handleClosePasswordDialog();
        // Log out the user after password change
        useAuthStore.getState().logout();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Push notifications need browser permission, so they're handled apart from the
  // optimistic toggles. Extracted to keep handleSettingChange's cognitive complexity low.
  const handlePushNotificationToggle = async (value: boolean) => {
    setPushNotificationError(null);

    if (!isPushSupported) {
      setPushNotificationError('Your browser does not support push notifications.');
      return;
    }

    // Ask browser for permission (or read existing state)
    const effectiveEnabled = await togglePushNotifications(value);

    // Update the UI toggle to the actual outcome
    setSettings(prev => ({ ...prev, pushNotifications: effectiveEnabled }));

    if (value && !effectiveEnabled) {
      // User tried to enable but browser denied permission
      setPushNotificationError(
        'Browser notification permission was denied. ' +
        'Please enable notifications in your browser settings and try again.'
      );
      return;
    }

    if (effectiveEnabled) {
      setSuccess('Browser push notifications enabled!');
      setTimeout(() => setSuccess(null), 3000);
    }

    // Sync preference to backend
    if (!user) return;
    setSavingNotification(true);
    try {
      await userApi.updateNotificationPreferences(user.id, {
        emailNotification: settings.emailNotifications,
        pushNotifications: effectiveEnabled,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save notification preferences');
    } finally {
      setSavingNotification(false);
    }
  };

  const handleSettingChange = async (setting: string, value: boolean) => {
    // Handle push notifications separately — must request browser permission
    if (setting === 'pushNotifications') {
      await handlePushNotificationToggle(value);
      return;
    }

    // Optimistically update all other toggles
    setSettings(prev => ({
      ...prev,
      [setting]: value,
    }));

    // Only call backend for email notification preference toggles
    if (!user) return;

    if (setting === 'emailNotifications') {
      const newEmail = value;
      const newPush = settings.pushNotifications;

      setSavingNotification(true);
      setError(null);
      setSuccess(null);
      try {
        await userApi.updateNotificationPreferences(user.id, {
          emailNotification: newEmail,
          pushNotifications: newPush,
        });

        setSuccess('Notification preferences updated');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to save notification preferences');
        // revert on failure
        setSettings(prev => ({
          ...prev,
          [setting]: !value,
        }));
      } finally {
        setSavingNotification(false);
      }
    }
  };

  const handleRefreshAnalytics = async () => {
    setRefreshing(true);
    setError(null);
    setRefreshSuccess(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/analytics-aggregation/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setRefreshSuccess('Analytics data refreshed successfully!');
        setTimeout(() => {
          setRefreshSuccess(null);
        }, 5000);
      } else {
        setError(result.error || 'Failed to refresh analytics');
      }
    } catch (error: any) {
      setError('Failed to refresh analytics. Please try again.');
      console.error('Analytics refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleFullRebuild = async () => {
    const confirmed = globalThis.confirm(
      'FULL REBUILD will TRUNCATE every aggregate table and re-derive all aggregates from scratch.\n\n' +
      'This is destructive and may take a long time depending on shipment volume.\n\n' +
      'Are you sure you want to proceed?'
    );
    if (!confirmed) return;

    setRefreshingFull(true);
    setError(null);
    setFullRebuildSuccess(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/analytics-aggregation/full-rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setFullRebuildSuccess('FULL rebuild completed! All aggregates rebuilt from scratch and filter hierarchy refreshed.');
        setTimeout(() => {
          setFullRebuildSuccess(null);
        }, 8000);
      } else {
        setError(result.error || 'Failed to perform full rebuild');
      }
    } catch (error: any) {
      setError('Failed to perform full rebuild. Please try again.');
      console.error('Full rebuild error:', error);
    } finally {
      setRefreshingFull(false);
    }
  };

  if (!user) {
    return (
      <Box>
        <Typography variant="h4">Settings</Typography>
        <Typography color="error">User data not available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader description="Manage your account settings and preferences." />

      <Grid container spacing={3}>
        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Security /> Security Settings
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <VpnKey />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Change Password"
                    secondary="Update your account password"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleChangePassword}
                    sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
                  >
                    Change
                  </Button>
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <ListItem>
                  <ListItemIcon>
                    <Smartphone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Two-Factor Authentication"
                    secondary={
                      twoFactorEnabled
                        ? 'Enabled - Your account is protected with 2FA'
                        : 'Add an extra layer of security to your account'
                    }
                  />
                  {(() => {
                    if (checking2FAStatus) {
                      return <CircularProgress size={24} />;
                    }
                    if (twoFactorEnabled) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle color="success" />
                          <Typography variant="body2" color="success.main">
                            Enabled
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDisable2FA}
                          >
                            Disable
                          </Button>
                        </Box>
                      );
                    }
                    return (
                      <Button
                        variant="outlined"
                        onClick={handleEnable2FA}
                        sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
                      >
                        Enable
                      </Button>
                    );
                  })()}
                </ListItem>

              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Notifications /> Notification Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                In-app notifications always appear under the bell icon. These settings control
                the extra channels.
              </Typography>

              <List>
                <ListItem>
                  <ListItemText 
                    primary="Email Notifications"
                    secondary="Receive notifications via email"
                  />
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    disabled={savingNotification}
                    color="primary"
                  />
                </ListItem>

                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <ListItemText
                      primary="Browser Push Notifications"
                      secondary={(() => {
                        if (!isPushSupported) return 'Not supported by your browser';
                        if (browserPermission === 'denied') return 'Blocked by browser — update in browser settings';
                        if (browserPermission === 'granted' && settings.pushNotifications) return 'Enabled — new notifications also pop up in your browser';
                        if (browserPermission === 'granted' && !settings.pushNotifications) return 'Off — browser permission still active';
                        return 'Toggle on to also get browser pop-ups for new notifications';
                      })()}
                      secondaryTypographyProps={{
                        sx: {
                          color: (() => {
                            if (browserPermission === 'denied') return theme.palette.error.main;
                            if (browserPermission === 'granted' && settings.pushNotifications) return theme.palette.success.main;
                            return undefined;
                          })(),
                        },
                      }}
                    />
                    <Tooltip
                      title={(() => {
                        if (!isPushSupported) return 'Your browser does not support push notifications';
                        if (browserPermission === 'denied') return 'Notification permission is blocked. Open browser settings to allow it.';
                        return 'Toggle browser push notifications';
                      })()}
                    >
                      <span>
                        <Switch
                          checked={settings.pushNotifications}
                          onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                          disabled={savingNotification || !isPushSupported || browserPermission === 'denied'}
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                  {pushNotificationError && (
                    <Alert
                      severity="error"
                      sx={{ mt: 1, width: '100%' }}
                      onClose={() => setPushNotificationError(null)}
                    >
                      {pushNotificationError}
                    </Alert>
                  )}
                  {browserPermission === 'granted' && !settings.pushNotifications && (
                    <Alert
                      severity="info"
                      sx={{ mt: 1, width: '100%', fontSize: '0.78rem' }}
                      icon={false}
                    >
                      No browser pop-ups will be shown. To fully revoke browser permission, click the lock/info icon in your browser's address bar and set Notifications to <strong>Block</strong>.
                    </Alert>
                  )}
                </ListItem>

              </List>
            </CardContent>
          </Card>
        </Grid>


        {/* Analytics Aggregation - MasterAdmin Only */}
        {user.accountType === AccountType.MasterAdmin && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Analytics /> Analytics Aggregation
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {refreshSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefreshSuccess(null)}>
                    {refreshSuccess}
                  </Alert>
                )}

                {fullRebuildSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFullRebuildSuccess(null)}>
                    {fullRebuildSuccess}
                  </Alert>
                )}

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Refresh />
                    </ListItemIcon>
                    <ListItemText
                      primary="Refresh Analytics Data (Incremental)"
                      secondary="Picks up only new/changed shipments since the last run, then refreshes the filter hierarchy. Automatically runs daily at 03:05 UTC."
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRefreshAnalytics}
                      disabled={refreshing || refreshingFull}
                      startIcon={refreshing ? <Refresh /> : <CheckCircle />}
                      sx={{ minWidth: 140 }}
                    >
                      {refreshing ? 'Refreshing...' : 'Refresh Now'}
                    </Button>
                  </ListItem>

                  <Divider sx={{ my: 2 }} />

                  <ListItem>
                    <ListItemIcon>
                      <RestartAlt />
                    </ListItemIcon>
                    <ListItemText
                      primary="FULL Rebuild (Complete Refresh)"
                      secondary="Truncates every aggregate table and re-derives all aggregates from scratch, then refreshes the filter hierarchy. Use only to fix data issues. WARNING: destructive — may take several hours for large datasets."
                      secondaryTypographyProps={{
                        sx: {
                          color: theme.palette.warning.main,
                          fontWeight: 500
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handleFullRebuild}
                      disabled={refreshing || refreshingFull}
                      startIcon={<RestartAlt />}
                      sx={{ minWidth: 180 }}
                    >
                      {refreshingFull ? 'Rebuilding...' : 'Full Rebuild'}
                    </Button>
                  </ListItem>
                </List>

                <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.info.light + '20', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>What gets refreshed (both buttons):</strong>
                    <br />
                    • Emissions analytics — daily, monthly, yearly (12 grouping shapes each)
                    <br />
                    • Statistics aggregates — daily, monthly, yearly, YTD (transactions, geolocations, shipments per company)
                    <br />
                    • GHG reporting aggregates — daily, monthly, yearly, YTD (emissions per transport mode)
                    <br />
                    • Cross-licensee rollup rows (company_id = 0) for unfiltered master-admin views
                    <br />
                    • Hierarchical filter cache (API keys → sub-companies → branches → dropdown values)
                    <br />
                    <br />
                    <strong>Incremental:</strong> Picks up shipments past the last cursor only. Typically seconds to minutes.
                    <br />
                    <strong>Full Rebuild:</strong> Truncates every aggregate table and starts from epoch. Hours for full history. Refuses to start if another run is already in progress.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey />
          Change Password
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                disabled={loading}
                helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.info.light + '20', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Password Requirements:</strong>
              <br />
              • At least 8 characters long
              <br />
              • Contains uppercase and lowercase letters
              <br />
              • Contains at least one number
              <br />
              • Contains at least one special character (!@#$%^&*)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={loading} startIcon={<Cancel />} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleSavePassword} 
            disabled={loading} 
            variant="contained"
            startIcon={<Save />}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Two-Factor Authentication Setup Dialog */}
      <TwoFactorSetupDialog
        open={twoFactorSetupOpen}
        onClose={() => setTwoFactorSetupOpen(false)}
        onSuccess={handle2FASetupSuccess}
      />

      {/* Two-Factor Authentication Disable Dialog */}
      <TwoFactorDisableDialog
        open={twoFactorDisableOpen}
        onClose={() => setTwoFactorDisableOpen(false)}
        onSuccess={handle2FADisableSuccess}
      />

      {/* Success Alert */}
      {success && !changePasswordDialogOpen && (
        <Alert
          severity="success"
          sx={{
            position: 'fixed',
            top: 80,
            right: 24,
            minWidth: 300,
            zIndex: 9999,
          }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default SettingsPage;